import { motion } from "framer-motion";
import { Target, Award, CheckCircle2, Building, TrendingUp, ShieldCheck, Maximize } from "lucide-react";

// Animation variants
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const itemXVariants = {
  hidden: { opacity: 0, x: -25 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
};

// Why Us Section
const WhyUsSection = () => {
  const points = [
    {
      icon: Target,
      title: "Spécialisation Mobile & IA",
      description: "Expertise pointue sur les enjeux complexes de vos systèmes critiques."
    },
    {
      icon: Award,
      title: "Delivery maîtrisé",
      description: "Exécution éprouvée avec méthodologie rigoureuse et automatisation des tests."
    },
    {
      icon: Building,
      title: "Groupe Proxiad",
      description: "L'agilité d'experts adossée aux garanties d'un groupe reconnu."
    }
  ];

  const benefits = [
    "Maîtrise absolue du risque technique",
    "Gouvernance et engagement de résultat",
    "Excellence continue en production"
  ];

  const pillars = [
    {
      title: "Ingénierie de pointe",
      description: "Des cycles de développement rigoureux conciliant vélocité de delivery et robustesse architecturale.",
      icon: <TrendingUp className="w-6 h-6 text-lenvia-blue" />
    },
    {
      title: "Security by Design",
      description: "Une posture souveraine et conforme aux exigences réglementaires et normatives les plus strictes.",
      icon: <ShieldCheck className="w-6 h-6 text-lenvia-blue" />
    },
    {
      title: "Haute Disponibilité",
      description: "Des solutions conçues nativement pour absorber la criticité et l'hyper-volumétrie.",
      icon: <Maximize className="w-6 h-6 text-lenvia-blue" />
    }
  ];

  return (
    <section className="section-spacing relative">
      {/* Background - LIGHTER */}
      <div className="absolute inset-0 bg-[#070B1A]" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#070B1A] via-[#0C1225]/60 to-[#070B1A]" />

      <div id="qui-sommes-nous" className="lenvia-container relative z-10">
        <div className="grid md:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2.5 mb-6">
              <Award className="w-4 h-4 text-lenvia-violet" />
              <span className="text-xs font-bold tracking-[0.2em] text-slate-400 uppercase">
                Qui sommes-nous ?
              </span>
            </span>
            
            <h2 
              className="font-heading text-3xl lg:text-[2.75rem] font-bold tracking-tight text-white mb-6 leading-[1.08]" 
              data-testid="whyus-title"
            >
              L'ingénierie logicielle
              <br />
              <span className="gradient-text">au service de vos enjeux critiques</span>
            </h2>
            
            <p className="text-base text-slate-300 mb-4 max-w-md leading-relaxed" data-testid="whyus-description">
              Lenvia opère à l'intersection du conseil stratégique et de l'ingénierie de pointe. Nous concevons, déployons et sécurisons les systèmes d'information qui ne peuvent pas échouer.
            </p>
            <p className="text-base text-slate-400 mb-8 max-w-md leading-relaxed">
              Adossés à l'envergure du groupe Proxiad, nous offrons aux directions techniques l'agilité d'un cabinet d'experts spécialisés alliée à la fiabilité d'un partenaire industriel de tout premier plan.
            </p>

            {/* Benefits List */}
            <div className="space-y-6">
              <h4 className="font-heading text-lg font-bold text-white">Ce que vous obtenez</h4>
              <motion.div
                className="space-y-4"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit}
                    variants={itemXVariants}
                    className="flex items-center gap-4 p-5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-lenvia-success/30 hover:bg-white/[0.05] transition-all cursor-default group"
                    data-testid={`benefit-${index}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-lenvia-success/15 border border-lenvia-success/25 flex items-center justify-center group-hover:bg-lenvia-success/20 transition-colors">
                      <CheckCircle2 className="w-5 h-5 text-lenvia-success" />
                    </div>
                    <span className="text-white font-semibold text-base">{benefit}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>

          {/* Right - Image & Points */}
          <motion.div
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            {/* Team in action - Premium Illustration */}
            <div className="relative mb-10 rounded-2xl overflow-hidden illustration-container h-64 lg:h-72 group">
              <img
                src="https://images.unsplash.com/photo-1632910121591-29e2484c0259?w=800&q=75"
                alt="Équipe Lenvia en collaboration technique"
                loading="lazy"
                className="w-full h-full object-cover scale-105 group-hover:scale-100 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070B1A] via-[#070B1A]/50 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#070B1A]/30 to-transparent" />
              <div className="absolute inset-0 bg-lenvia-blue/[0.05] mix-blend-overlay" />
              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <p className="text-xs font-semibold text-white/60 tracking-wider uppercase">Culture engineering</p>
              </div>
            </div>

            {/* Points */}
            <motion.div
              className="space-y-5"
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              {points.map((point, index) => (
                <motion.div
                  key={point.title}
                  variants={itemVariants}
                  className="glass-card p-6 flex items-start gap-5 group"
                  data-testid={`point-${index}`}
                >
                  <div className="icon-container flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <point.icon className="w-5 h-5 text-lenvia-violet" />
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-white mb-1.5 text-lg">{point.title}</h4>
                    <p className="text-sm text-slate-400 leading-relaxed">{point.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Technical Partner Pillars */}
        <div className="mt-24 pt-16 border-t border-white/[0.06]">
          <div className="text-center mb-16">
            <h3 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight text-white mb-4">
              L'exigence au cœur de vos{" "}
              <span className="gradient-text">opérations tactiques</span>
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
            {pillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 25 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group"
              >
                <div className="glass-card p-8 flex flex-col items-center text-center h-full">
                  <div className="icon-container-lg mb-6 group-hover:scale-110 transition-transform duration-300">
                    {pillar.icon}
                  </div>
                  <h4 className="font-heading text-xl font-bold text-white mb-4">{pillar.title}</h4>
                  <p className="text-base text-slate-400 leading-relaxed">{pillar.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyUsSection;
